import { useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";

import { useDialogs } from "@src/hooks/dialog";
import { useQuery } from "@tanstack/react-query";

const UNSAFE_ACTIONS = ["edit", "new"];
const TWO_DAYS_IN_SECONDS = 2 * 24 * 60 * 60;
const ONE_DAY_IN_SECONDS = 24 * 60 * 60;

interface VersionInfo {
  originCommitHash: string;
  localCommitHash: string;
  buildTime: number;
  hasUncommittedChanges: boolean;
}

export const useIsStaleVersion = () => {
  const currentBuildTime = parseInt(__BUILD_TIME__);
  const latestVersionQuery = useQuery({
    queryKey: ["latest-version-check"],
    meta: {
      onError: { any: "ignore" }, // Suppress error toasts
    },
    queryFn: async (): Promise<VersionInfo | null> => {
      try {
        const response = await fetch("/latest-version.txt", {
          cache: "no-store",
        });
        if (!response.ok) {
          return null;
        }

        const versionInfo = (await response.json()) as VersionInfo;
        if (!versionInfo.buildTime || isNaN(versionInfo.buildTime)) {
          return null;
        }
        return versionInfo;
      } catch (error) {
        return null;
      }
    },
    refetchInterval: 5 * 60 * 1000,
    refetchOnWindowFocus: true,
    retry: false,
  });

  const isStaleAutoRefresh = Boolean(
    latestVersionQuery.data &&
    currentBuildTime + TWO_DAYS_IN_SECONDS < latestVersionQuery.data.buildTime
  );
  const isStaleNudge = Boolean(
    latestVersionQuery.data &&
    currentBuildTime + ONE_DAY_IN_SECONDS < latestVersionQuery.data.buildTime
  );

  return { isStaleAutoRefresh, isStaleNudge };
};

export const useVersionCheck = () => {
  const { openDialog } = useDialogs();
  const [searchParams] = useSearchParams();
  const currentBuildTime = parseInt(__BUILD_TIME__);
  const action = searchParams.get("action");
  const isUnsafeRoute = action ? UNSAFE_ACTIONS.includes(action) : false;
  const { isStaleAutoRefresh } = useIsStaleVersion();

  // Check for forced minimum version - return null on error to silently fail
  const minVersionQuery = useQuery({
    queryKey: ["min-version-check"],
    meta: {
      onError: { any: "ignore" }, // Suppress error toasts
    },
    queryFn: async (): Promise<number | null> => {
      try {
        const stage = import.meta.env.VITE_STAGE;
        if (stage === "local") {
          return null;
        }

        const response = await fetch(
          `https://static.intelligems.io/${stage}/min-version.txt?t=${Date.now()}`,
          { cache: "no-store" }
        );

        if (!response.ok) {
          return null;
        }
        const text = await response.text();
        const version = parseInt(text, 10);
        if (isNaN(version)) {
          return null;
        }
        return version;
      } catch (error) {
        return null;
      }
    },
    refetchInterval: 5 * 60 * 1000,
    refetchOnWindowFocus: true,
    retry: false, // Don't retry on errors - will retry via refetchInterval
  });

  // Auto-refresh on safe navigation when build is stale (two days old, safety net)
  useEffect(() => {
    if (isStaleAutoRefresh) {
      const handleClick = (e: MouseEvent) => {
        const target = e.target as HTMLElement;
        const link = target.closest("a");
        if (link && link.href) {
          console.log(
            "Navigation link clicked with stale version. Reloading to get latest updates..."
          );
          e.preventDefault();
          window.location.href = link.href;
        }
      };

      const handlePopState = () => {
        console.log(
          "Browser navigation detected with stale version. Reloading to get latest updates..."
        );
        window.location.reload();
      };

      document.addEventListener("click", handleClick, true);
      window.addEventListener("popstate", handlePopState);

      // Cleanup function
      return () => {
        document.removeEventListener("click", handleClick, true);
        window.removeEventListener("popstate", handlePopState);
      };
    }
  }, [isStaleAutoRefresh]);

  // Handle forced minimum version (show non-closable dialog)
  useEffect(() => {
    if (
      minVersionQuery.data !== null &&
      minVersionQuery.data !== undefined &&
      currentBuildTime < minVersionQuery.data
    ) {
      console.warn(
        `Version outdated. Current: ${currentBuildTime}, Min required: ${minVersionQuery.data}. Showing force reload dialog...`
      );
      if (isUnsafeRoute) {
        toast.warning(
          "Your experience might be disrupted - please save/record important work in case of failure and reload your page.",
          {
            toastId: "force-reload-unsafe-route",
            autoClose: false,
            closeOnClick: false,
            closeButton: false,
            position: "top-right",
          }
        );
        return;
      }
      openDialog("forceReload");
    }
  }, [minVersionQuery.data, currentBuildTime, openDialog, isUnsafeRoute]);
};
